// Remote Punch — the selected company's settings: on/off, the accuracy limit,
// the monitoring period, and who receives its alerts (searched by name, code
// or email; several people). Served by /api/remote-punch/settings?clientName=
// (superadmin only).
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MapPinned, Save, RefreshCw, StopCircle, RotateCcw, X, ShieldCheck } from 'lucide-react';
import { LanguageContext } from '../../../constant/LanguageContext';
import AlertModal from '../../../components/AlertModal';
import ReusableDropdown from '../../../components/ReusableDropdown';
import { remotePunchApi } from '../../../components/RemotePunch/remotePunchApi';
import { rpText } from '../../../components/RemotePunch/remotePunchText';

const formatDate = (iso, language) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(language === 'ar' ? 'ar-u-nu-latn' : 'en-GB', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const MAX_RECIPIENTS = 10; // same limit as the server

/** Order-insensitive identity of a recipient list, for the unsaved-changes check. */
const idsKey = (list) => (list || []).map((r) => r.id).sort((a, b) => a - b).join(',');

const recipientName = (r, language) =>
  (language === 'ar' ? r.nameAr || r.name : r.name || r.nameAr) || r.email || `#${r.id}`;

/** "Name · code · email" for the search results. */
const recipientLine = (r, language) =>
  [recipientName(r, language), r.clientCode || r.infinityCode, r.email]
    .filter((v, i, all) => v && all.indexOf(v) === i)
    .join(' · ');

const RemotePunchSettings = ({ theme, styles, clientName }) => {
  const { language } = useContext(LanguageContext) || { language: 'en' };
  const t = rpText(language);
  const light = theme === 'light';
  const colors = {
    text: light ? '#0f172a' : '#ffffff',
    muted: light ? '#64748b' : 'rgba(255,255,255,0.6)',
    border: light ? '#e2e8f0' : 'rgba(255,255,255,0.1)',
    input: light ? '#ffffff' : 'rgba(255,255,255,0.05)',
  };

  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({ enabled: true, accuracyThresholdMeters: '', monitoringDays: '', recipients: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState({ open: false, title: '', message: '', variant: 'info', showCancel: false, onConfirm: null });

  const apply = useCallback((data) => {
    setSettings(data);
    setDraft({
      enabled: data?.enabled !== false,
      accuracyThresholdMeters: String(data?.accuracyThresholdMeters ?? ''),
      monitoringDays: String(data?.monitoringDays ?? ''),
      recipients: Array.isArray(data?.recipients) ? data.recipients : [],
    });
  }, []);

  // Recipient search — on the server (a company can have thousands of
  // accounts), debounced, latest answer wins.
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [pickerKey, setPickerKey] = useState(0);
  const searchSeq = useRef(0);
  useEffect(() => {
    if (!clientName) return undefined;
    const seq = ++searchSeq.current;
    const id = setTimeout(() => {
      remotePunchApi.searchRecipients(clientName, searchTerm.trim())
        .then((list) => { if (seq === searchSeq.current) setResults(Array.isArray(list) ? list : []); })
        .catch(() => { if (seq === searchSeq.current) setResults([]); });
    }, 300);
    return () => clearTimeout(id);
  }, [clientName, searchTerm]);

  const addRecipient = (r) => {
    setDraft((d) => (d.recipients.some((x) => x.id === r.id) || d.recipients.length >= MAX_RECIPIENTS
      ? d
      : { ...d, recipients: [...d.recipients, r] }));
    setPickerKey((k) => k + 1); // fresh, empty search box for the next one
  };
  const removeRecipient = (id) => setDraft((d) => ({ ...d, recipients: d.recipients.filter((r) => r.id !== id) }));

  const showError = useCallback((err) => setDialog({
    open: true, title: t.error, message: err?.message || String(err), variant: 'error', showCancel: false, onConfirm: null,
  }), [t.error]);

  useEffect(() => {
    if (!clientName) return undefined;
    let cancelled = false;
    setLoading(true);
    setSettings(null);
    remotePunchApi.getSettings(clientName)
      .then((data) => { if (!cancelled) apply(data); })
      .catch((err) => { if (!cancelled) showError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientName, apply, showError]);

  const dirty = settings && (
    draft.enabled !== (settings.enabled !== false) ||
    draft.accuracyThresholdMeters !== String(settings.accuracyThresholdMeters ?? '') ||
    draft.monitoringDays !== String(settings.monitoringDays ?? '') ||
    idsKey(draft.recipients) !== idsKey(settings.recipients)
  );

  const save = async () => {
    setBusy(true);
    try {
      apply(await remotePunchApi.updateSettings(clientName, {
        enabled: draft.enabled,
        accuracyThresholdMeters: Number(draft.accuracyThresholdMeters),
        monitoringDays: Number(draft.monitoringDays),
        alertRecipientUserIds: draft.recipients.map((r) => r.id),
      }));
      setDialog({ open: true, title: t.success, message: t.saved, variant: 'success', showCancel: false, onConfirm: null });
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const monitoringAction = (action) => setDialog({
    open: true,
    title: action === 'end' ? t.endMonitoring : t.restartMonitoring,
    message: action === 'end' ? t.confirmEnd : t.confirmRestart,
    variant: 'warning',
    showCancel: true,
    onConfirm: async () => {
      setBusy(true);
      try {
        apply(await remotePunchApi.changeMonitoring(clientName, action));
      } catch (err) {
        showError(err);
      } finally {
        setBusy(false);
      }
    },
  });

  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const status = !settings
    ? ''
    : !settings.monitoringStartedAt
      ? t.monitoringStatusNotStarted
      : settings.monitoring
        ? t.monitoringStatusOn(formatDate(settings.monitoringEndsAt, language))
        : t.monitoringStatusOff(formatDate(settings.monitoringEndsAt, language));
  const statusColor = !settings?.monitoringStartedAt ? colors.muted : settings?.monitoring ? '#d97706' : '#059669';

  const label = { display: 'block', fontSize: '13px', fontWeight: 600, color: colors.text, marginBottom: '6px' };
  const hint = { fontSize: '12px', color: colors.muted, marginTop: '6px', lineHeight: 1.5 };
  const input = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '9px', fontSize: '14px',
    border: `1.5px solid ${colors.border}`, background: colors.input, color: colors.text, outline: 'none',
  };
  const button = (primary, disabled) => ({
    display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', fontSize: '13.5px', fontWeight: 600,
    borderRadius: '9px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
    border: primary ? 'none' : `1.5px solid ${colors.border}`,
    background: primary ? 'linear-gradient(135deg, #2e7cba, #1e5f8f)' : colors.input,
    color: primary ? '#ffffff' : colors.text,
  });

  return (
    <div style={{
      background: light ? 'rgba(46,124,186,0.03)' : 'rgba(46,124,186,0.06)',
      border: light ? '1.5px solid rgba(46,124,186,0.18)' : '1.5px solid rgba(46,124,186,0.25)',
      borderRadius: '14px',
      padding: '20px',
      marginBottom: '20px',
    }} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <AlertModal
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
        showCancel={dialog.showCancel}
        onConfirm={() => { const fn = dialog.onConfirm; closeDialog(); fn?.(); }}
        onClose={closeDialog}
        theme={theme}
        language={language}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(46,124,186,0.15), rgba(46,124,186,0.08))', border: '1px solid rgba(46,124,186,0.2)', color: '#2e7cba',
        }}>
          <MapPinned size={22} />
        </div>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: colors.text, margin: 0 }}>{t.settingsTitle}</h2>
          <p style={{ fontSize: '13px', color: colors.muted, margin: '2px 0 0' }}>{t.settingsSubtitle}</p>
        </div>
        <button type="button" style={button(true, busy || !dirty)} disabled={busy || !dirty} onClick={save}>
          {busy ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
          {busy ? t.saving : t.save}
        </button>
      </div>

      {loading ? (
        <div style={{ color: colors.muted, fontSize: '14px' }}>{t.loading}</div>
      ) : settings && (
        <>
          <div
            role="switch"
            aria-checked={draft.enabled}
            tabIndex={0}
            onClick={() => setDraft((d) => ({ ...d, enabled: !d.enabled }))}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                setDraft((d) => ({ ...d, enabled: !d.enabled }));
              }
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', marginBottom: '18px',
              borderRadius: '12px', cursor: 'pointer', border: `1px solid ${colors.border}`,
              background: draft.enabled ? (light ? 'rgba(46,124,186,0.06)' : 'rgba(46,124,186,0.12)') : 'transparent',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: colors.text }}>{t.featureEnabled}</div>
              <div style={{ ...hint, marginTop: '4px' }}>{t.featureEnabledHint}</div>
            </div>
            <div style={{ ...styles.toggle(draft.enabled), direction: 'ltr' }}>
              <div style={styles.toggleKnob(draft.enabled)} />
            </div>
          </div>

          <div style={{
            opacity: draft.enabled ? 1 : 0.45,
            pointerEvents: draft.enabled ? 'auto' : 'none',
            transition: 'opacity 0.2s',
          }} aria-disabled={!draft.enabled}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px' }}>
            <div>
              <label style={label} htmlFor="rp-accuracy">{t.accuracyLimit}</label>
              <input id="rp-accuracy" type="number" min={10} max={5000} style={input}
                value={draft.accuracyThresholdMeters}
                onChange={(e) => setDraft((d) => ({ ...d, accuracyThresholdMeters: e.target.value }))} />
              <div style={hint}>{t.accuracyLimitHint}</div>
            </div>
            <div>
              <label style={label} htmlFor="rp-days">{t.monitoringDays}</label>
              <input id="rp-days" type="number" min={0} max={365} style={input}
                value={draft.monitoringDays}
                onChange={(e) => setDraft((d) => ({ ...d, monitoringDays: e.target.value }))} />
              <div style={hint}>{t.monitoringDaysHint}</div>
            </div>
          </div>

          {/* Who receives the alerts: search, pick, repeat. */}
          <div style={{ marginTop: '18px' }}>
            <label style={label} htmlFor="rp-recipient">
              {t.alertAccount}
              <span style={{ fontWeight: 500, color: colors.muted, marginInlineStart: 8 }}>
                {draft.recipients.length}/{MAX_RECIPIENTS}
              </span>
            </label>
            <ReusableDropdown
              key={pickerKey}
              id="rp-recipient"
              items={results.filter((r) => !draft.recipients.some((x) => x.id === r.id))}
              value=""
              onSelect={addRecipient}
              onSearchChange={setSearchTerm}
              filterFunction={() => true}
              getItemKey={(r) => String(r.id)}
              getItemLabel={(r) => recipientLine(r, language)}
              renderItem={(r) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  {r.role === 'superadmin' && <ShieldCheck size={14} color="#2e7cba" style={{ flexShrink: 0 }} />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{recipientLine(r, language)}</span>
                </div>
              )}
              placeholder={draft.recipients.length >= MAX_RECIPIENTS ? t.alertMax(MAX_RECIPIENTS) : t.alertSearch}
              emptyMessage={t.alertSearchEmpty}
              disabled={draft.recipients.length >= MAX_RECIPIENTS}
              maxItems={50}
              theme={theme}
              language={language}
            />

            {draft.recipients.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                {draft.recipients.map((r) => (
                  <span key={r.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 8px 6px 12px',
                    borderRadius: '999px', fontSize: '13px', fontWeight: 600, color: colors.text,
                    background: light ? 'rgba(46,124,186,0.08)' : 'rgba(46,124,186,0.16)',
                    border: '1px solid rgba(46,124,186,0.3)', maxWidth: '100%',
                  }}>
                    {r.role === 'superadmin' && <ShieldCheck size={14} color="#2e7cba" style={{ flexShrink: 0 }} />}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {recipientName(r, language)}
                      {(r.clientCode || r.infinityCode) && (
                        <span style={{ fontWeight: 500, color: colors.muted }}> · {r.clientCode || r.infinityCode}</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeRecipient(r.id)}
                      aria-label={`${t.removeRecipient} ${recipientName(r, language)}`}
                      title={t.removeRecipient}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22,
                        borderRadius: '50%', border: 'none', cursor: 'pointer', flexShrink: 0,
                        background: light ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.1)', color: colors.muted,
                      }}
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ ...hint, color: '#d97706' }}>{t.alertAccountNone}</div>
            )}
            <div style={hint}>{t.alertAccountHint}</div>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '20px', padding: '12px 14px',
            borderRadius: '10px', border: `1px solid ${colors.border}`,
          }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: '200px', fontSize: '13.5px', fontWeight: 600, color: colors.text }}>{status}</span>
            {settings.monitoring && settings.monitoringStartedAt && (
              <button type="button" style={button(false, busy)} disabled={busy} onClick={() => monitoringAction('end')}>
                <StopCircle size={14} /> {t.endMonitoring}
              </button>
            )}
            <button type="button" style={button(false, busy)} disabled={busy} onClick={() => monitoringAction('restart')}>
              <RotateCcw size={14} /> {t.restartMonitoring}
            </button>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RemotePunchSettings;
